import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, imgProps } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function JournalArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api
      .get(`/journal/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-4xl font-light text-[#262019]">This story is still being written</h1>
        <Link to="/journal" className="btn-gold mt-8" data-testid="article-back">
          Back to the Journal
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="article-loading">
        <p className="text-sm tracking-[0.2em] text-[#7C7163]">OPENING THE JOURNAL…</p>
      </div>
    );
  }

  return (
    <article className="pb-24 pt-40 lg:pb-32" data-testid="journal-article-page">
      <div className="mx-auto max-w-3xl px-6">
        <Link to="/journal" data-testid="back-to-journal" className="group flex w-fit items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#7C7163] transition-colors duration-300 hover:text-[#A58343]">
          <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-x-1" />
          All stories
        </Link>
        <Reveal>
          <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] uppercase tracking-[0.25em] text-[#7C7163]">
            <span className="whitespace-nowrap">{formatDate(article.date)}</span>
            <span className="h-px w-6 bg-[#A58343]/40" />
            <span className="whitespace-nowrap">{article.read_time}</span>
            <span className="h-px w-6 bg-[#A58343]/40" />
            <span className="whitespace-nowrap">{article.author}</span>
          </div>
          <h1 className="mt-6 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl" data-testid="article-title">
            {article.title}
          </h1>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="mx-auto mt-12 max-w-5xl px-6">
          <div className="bg-[#F4EEE3]">
            <img {...imgProps(article.image, "100vw")} alt={article.title} className="block h-auto w-full" />
          </div>
        </div>
      </Reveal>

      <div className="mx-auto mt-16 max-w-3xl space-y-8 px-6" data-testid="article-content">
        {article.content.map((para, i) => (
          <p key={i} className={`leading-relaxed ${i === 0 ? "font-serif text-2xl font-light text-[#262019]" : "text-base text-[#7C7163]"}`}>
            {para}
          </p>
        ))}
        <div className="border-t border-[#A58343]/20 pt-10">
          <p className="font-serif text-2xl font-light text-[#262019]">
            Curious to see the pieces up close?
          </p>
          <Link to="/consultation" className="btn-gold mt-6" data-testid="article-book-consultation">
            Book a Consultation
          </Link>
        </div>
      </div>
    </article>
  );
}
